import {
  createContext,
  use,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';

import { PreviewLayover } from '@/modules/outputs/components/PreviewLayover';
import { downloadOutput } from '@/modules/outputs/utils/download';
import type { TResolvedFile } from '@/modules/outputs/types';
import { captureException } from '@/modules/sentry';

import { useAgentArtifactContent } from '../hooks/use-agent-artifact-content';
import type { IAgentArtifactFile } from '../utils/artifact-file';

interface IAgentArtifactViewerContext {
  /** Fetches the file's bytes and opens the viewer on it. */
  openFile: (file: IAgentArtifactFile) => void;
  /** URI currently being fetched, so its card can show a spinner. */
  loadingUri: string | null;
}

/**
 * Undefined outside a provider. A file card rendered on a surface without one
 * degrades to a non-interactive chip rather than throwing — the card is built
 * by a Rain tool renderer, which can in principle be mounted anywhere.
 */
// Scoped wiring, not app state. The consumer (a file card) is produced by a
// Rain tool renderer — a plain function the chat runtime calls while building
// messages — so there is no prop path from the panel to the card. A store would
// also be wrong: `openFile` is bound to ONE agent + thread, and the playground
// can mount a second chat, which a module-level store would let share a viewer.
// eslint-disable-next-line no-restricted-syntax -- reason: see above
const AgentArtifactViewerContext = createContext<
  IAgentArtifactViewerContext | undefined
>(undefined);

export const useAgentArtifactViewer = ():
  | IAgentArtifactViewerContext
  | undefined => use(AgentArtifactViewerContext);

interface IAgentArtifactViewerProviderProps {
  agentId: string;
  threadId: string;
  children: ReactNode;
}

/**
 * Owns the playground's single file viewer and hands its `openFile` down to the
 * artifact file cards through context.
 *
 * Context rather than props because the cards are produced by a Rain tool
 * renderer — a plain function the chat runtime calls while building messages,
 * far from this component's JSX. There is no prop path from here to there.
 *
 * One viewer for the whole chat, not one per card: only one file can be open at
 * a time, and mounting a portal per file card would put dozens of them in the
 * tree on a long thread.
 */
export function AgentArtifactViewerProvider({
  agentId,
  threadId,
  children,
}: IAgentArtifactViewerProviderProps) {
  const { t } = useTranslation('agent-builder');
  const fetchArtifactContent = useAgentArtifactContent(agentId, threadId);

  const [openedFile, setOpenedFile] = useState<IAgentArtifactFile | null>(null);
  const [previewValue, setPreviewValue] = useState<TResolvedFile | null>(null);
  const [loadingUri, setLoadingUri] = useState<string | null>(null);
  // Tags each open so a superseded or cancelled click can't pop a stale file
  // in later. Mirrors `useRainArtifactPreview`'s `previewRequestRef`.
  const requestRef = useRef(0);

  const closeViewer = useCallback(() => {
    requestRef.current += 1;
    setLoadingUri(null);
    setOpenedFile(null);
    setPreviewValue(null);
  }, []);

  const openFile = useCallback(
    (file: IAgentArtifactFile) => {
      const requestId = (requestRef.current += 1);

      // Open on a spinner immediately — the fetch is a round trip and a card
      // that looks inert until bytes arrive reads as a broken click.
      setOpenedFile(file);
      setPreviewValue(null);
      setLoadingUri(file.uri);

      void (async () => {
        try {
          const content = await fetchArtifactContent(file.uri);
          if (requestRef.current !== requestId) return;
          setLoadingUri(null);
          setPreviewValue({
            type: 'file-inline',
            name: file.filename,
            content,
          });
        } catch (error) {
          if (requestRef.current !== requestId) return;
          setLoadingUri(null);
          setOpenedFile(null);
          toast.error(t('agentBuilder.test.files.openError'));
          captureException(error as Error, {
            tags: {
              module: 'agent-builder',
              action: 'open_agent_artifact',
            },
            extra: { artifactUri: file.uri },
          });
        }
      })();
    },
    [fetchArtifactContent, t],
  );

  const value = useMemo(
    () => ({ openFile, loadingUri }),
    [openFile, loadingUri],
  );

  return (
    <AgentArtifactViewerContext value={value}>
      {children}
      {openedFile ? (
        <PreviewLayover
          isOpen
          isLoading={previewValue === null}
          loadingLabel={openedFile.filename}
          outputName={openedFile.filename}
          outputValue={previewValue}
          onClose={closeViewer}
          onDownload={downloadOutput}
        />
      ) : null}
    </AgentArtifactViewerContext>
  );
}
